import { computed, ref, type Ref } from "vue";
import type { TableStatus } from "@lemmonhq/types";
import { api } from "@app/data/api";

export type OpenTablesState = {
  /** Tables with at least one order that has not been paid yet */
  busyTables: Ref<TableStatus[]>;
  freeTables: Ref<TableStatus[]>;
  busyCount: Ref<number>;
  freeCount: Ref<number>;
  orderCount: Ref<number>;
  /** Money sitting on open tables, not yet part of the payment reports */
  totalAmount: Ref<number>;
  loading: Ref<boolean>;
  error: Ref<boolean>;
  refresh: () => Promise<void>;
};

/** Load current table state independently of report dates or service hours. */
export const useOpenTables = (): OpenTablesState => {
  const tables = ref<TableStatus[]>([]);
  const loading = ref(false);
  const error = ref(false);

  const busyTables = computed(() =>
    tables.value.filter((table) => !!table.table.is_busy),
  );
  const freeTables = computed(() =>
    tables.value.filter((table) => !table.table.is_busy),
  );
  const orderCount = computed(() =>
    busyTables.value.reduce(
      (total, table) => total + (table.orders?.length || 0),
      0,
    ),
  );
  const totalAmount = computed(() =>
    busyTables.value.reduce(
      (total, table) => total + (table.total_amount || 0),
      0,
    ),
  );

  const refresh = async () => {
    loading.value = true;
    error.value = false;
    try {
      tables.value = await api.getTablesMeta();
    } catch (e) {
      error.value = true;
      tables.value = [];
      console.error("Failed to fetch open tables:", e);
    } finally {
      loading.value = false;
    }
  };

  return {
    busyTables,
    freeTables,
    busyCount: computed(() => busyTables.value.length),
    freeCount: computed(() => freeTables.value.length),
    orderCount,
    totalAmount,
    loading,
    error,
    refresh,
  };
};
